import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Check, Baby, Heart, Moon, Sun, Clock, 
  Utensils, Loader2, Calendar, User, Mail, Phone, FileText,
  ChevronRight, Sparkles, Plus, Trash2
} from 'lucide-react';
import Logo from '../../components/common/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import apiService from '../../services/api';

// Opções para campos do formulário
const howBabySleepsOptions = [
  'No colo',
  'No peito',
  'Com mamadeira',
  'Sendo ninado',
  'No carrinho',
  'No carro',
  'Sozinho no berço',
  'Com ajuda mínima',
  'Outro'
];

const whereBabySleepsOptions = [
  'Berço no quarto dos pais',
  'Berço no próprio quarto',
  'Cama compartilhada',
  'Moisés',
  'Carrinho',
  'Outro'
];

const AdminManualReportPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Total de steps: 1 (Dados básicos) + 1 (Questionário) + 5 (Dias de rotina) + 1 (Revisão) = 8
  const totalSteps = 8;

  const stepLabels = [
    'Dados do Bebê',
    'Questionário',
    'Dia 1',
    'Dia 2', 
    'Dia 3',
    'Dia 4',
    'Dia 5',
    'Revisão'
  ];

  // Estado do formulário
  const [formData, setFormData] = useState({
    // Dados básicos
    babyName: '',
    babyAge: '',
    birthWeeks: '',
    birthDate: '',
    motherName: '',
    fatherName: '',
    email: '',
    whatsapp: '',
    // Questionário inicial
    sleepDifficulty: '',
    expectations: '',
    previousAttempts: '',
    healthConditions: '',
    heelPrickTest: '',
    snoring: '',
    ironSupplementation: '',
    feedingType: '',
    solidFoods: '',
    motorDevelopment: '',
    howBabySleeps: [], // Array para múltiplas seleções
    sleepLocation: '',
    transitionObject: '',
    transitionObjectDesc: '',
    caregiver: '',
    otherChildren: '',
    motherSleep: ''
  });

  // Estado dos 5 dias de rotina
  const [routineDays, setRoutineDays] = useState([
    { day: 1, wakeUpTime: '', bedTime: '', nightWakings: '', nightWakingsDuration: '', naps: [], noNaps: false, notes: '' },
    { day: 2, wakeUpTime: '', bedTime: '', nightWakings: '', nightWakingsDuration: '', naps: [], noNaps: false, notes: '' },
    { day: 3, wakeUpTime: '', bedTime: '', nightWakings: '', nightWakingsDuration: '', naps: [], noNaps: false, notes: '' },
    { day: 4, wakeUpTime: '', bedTime: '', nightWakings: '', nightWakingsDuration: '', naps: [], noNaps: false, notes: '' },
    { day: 5, wakeUpTime: '', bedTime: '', nightWakings: '', nightWakingsDuration: '', naps: [], noNaps: false, notes: '' }
  ]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoutineChange = (dayIndex, field, value) => {
    setRoutineDays(prev => prev.map((day, i) => 
      i === dayIndex ? { ...day, [field]: value } : day
    ));
  };

  // Funções para gerenciar sonecas
  const addNap = (dayIndex) => {
    setRoutineDays(prev => prev.map((day, i) => {
      if (i === dayIndex) {
        return {
          ...day,
          naps: [...(day.naps || []), { startTime: '', duration: '' }]
        };
      }
      return day;
    }));
  };

  const removeNap = (dayIndex, napIndex) => {
    setRoutineDays(prev => prev.map((day, i) => {
      if (i === dayIndex) {
        const newNaps = day.naps.filter((_, idx) => idx !== napIndex);
        return { ...day, naps: newNaps };
      }
      return day;
    }));
  };

  const updateNap = (dayIndex, napIndex, field, value) => {
    setRoutineDays(prev => prev.map((day, i) => {
      if (i === dayIndex) {
        const newNaps = day.naps.map((nap, idx) => {
          if (idx === napIndex) {
            return { ...nap, [field]: value };
          }
          return nap;
        });
        return { ...day, naps: newNaps };
      }
      return day;
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: // Dados básicos
        return formData.babyName && formData.babyAge && formData.motherName;
      case 2: // Questionário
        return formData.sleepDifficulty && formData.howBabySleeps.length > 0;
      case 3: // Dia 1
      case 4: // Dia 2
      case 5: // Dia 3
      case 6: // Dia 4
      case 7: // Dia 5
        const dayIndex = currentStep - 3;
        return routineDays[dayIndex].wakeUpTime && routineDays[dayIndex].bedTime;
      case 8: // Revisão
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      // 1. Criar o relatório
      const reportResponse = await apiService.createReport({
        baby_name: formData.babyName,
        mother_email: formData.email || `manual_${Date.now()}@sistema.com`
      });

      const reportId = reportResponse.id;

      // 2. Atualizar dados básicos
      await apiService.updateReportBasicInfo(reportId, {
        babyName: formData.babyName,
        babyAge: parseInt(formData.babyAge),
        birthWeeks: formData.birthWeeks ? parseInt(formData.birthWeeks) : null,
        birthDate: formData.birthDate,
        motherName: formData.motherName,
        fatherName: formData.fatherName,
        email: formData.email,
        whatsapp: formData.whatsapp
      });

      // 3. Enviar formulário inicial
      await apiService.updateInitialForm(reportId, {
        sleepDifficulty: formData.sleepDifficulty,
        expectations: formData.expectations,
        previousAttempts: formData.previousAttempts,
        healthConditions: formData.healthConditions,
        heelPrickTest: formData.heelPrickTest,
        snoring: formData.snoring,
        ironSupplementation: formData.ironSupplementation,
        feedingType: formData.feedingType,
        solidFoods: formData.solidFoods,
        motorDevelopment: formData.motorDevelopment,
        howBabySleeps: Array.isArray(formData.howBabySleeps) ? formData.howBabySleeps.join(', ') : formData.howBabySleeps,
        sleepLocation: formData.sleepLocation,
        transitionObject: formData.transitionObject === 'yes',
        transitionObjectDesc: formData.transitionObjectDesc,
        caregiver: formData.caregiver,
        otherChildren: formData.otherChildren,
        motherSleep: formData.motherSleep
      });

      // 4. Enviar os 5 dias de rotina
      for (const day of routineDays) {
        await apiService.updateRoutineDay(reportId, day.day, {
          date: new Date().toISOString().split('T')[0],
          wakeUpTime: day.wakeUpTime,
          bedTime: day.bedTime,
          nightWakings: day.nightWakings || null,
          nightWakingsDuration: day.nightWakingsDuration || null,
          naps: day.naps || [],
          noNaps: day.noNaps || false,
          routineDescription: day.notes,
          importantNotes: day.notes
        });
      }

      // 5. Navegar para o gerador de plano
      navigate(`/admin/reports/${reportId}/plan`);

    } catch (err) {
      console.error('Erro ao salvar relatório:', err);
      setError('Erro ao salvar o relatório. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // Renderizar step atual
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <StepBasicData formData={formData} onChange={handleInputChange} />;
      case 2:
        return <StepQuestionnaire formData={formData} onChange={handleInputChange} />;
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
        const dayIndex = currentStep - 3;
        return (
          <StepRoutineDay 
            day={routineDays[dayIndex]} 
            dayNumber={dayIndex + 1}
            onChange={(field, value) => handleRoutineChange(dayIndex, field, value)}
            onAddNap={addNap}
            onRemoveNap={removeNap}
            onUpdateNap={updateNap}
          />
        );
      case 8:
        return <StepReview formData={formData} routineDays={routineDays} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Header */}
      <header className="bg-white border-b border-[#FAE0D5] px-6 py-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/admin/dashboard">
              <Logo variant="v3SonoRotina" size="sm" className="cursor-pointer hover:opacity-80" />
            </Link>
          </div>
          <div className="text-sm text-[#6B5B8C]">
            Passo {currentStep} de {totalSteps}
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-[#FAE0D5] px-6 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {stepLabels.map((label, index) => (
              <div 
                key={index}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${
                  index + 1 === currentStep
                    ? 'bg-[#8F7AC5] text-white'
                    : index + 1 < currentStep
                    ? 'bg-[#7BC47F] text-white'
                    : 'bg-[#FAE0D5] text-[#6B5B8C]'
                }`}
              >
                {index + 1 < currentStep ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <span className="w-4 h-4 rounded-full bg-white/30 flex items-center justify-center text-[10px]">
                    {index + 1}
                  </span>
                )}
                <span className="hidden sm:inline">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Error message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="border-[#D98DAE] text-[#D98DAE]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Anterior
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-[#8F7AC5] hover:bg-[#7A68A8]"
            >
              Próximo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#7BC47F] hover:bg-[#5BA35F]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Criar e Gerar Plano
                </>
              )}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

// ==================== STEP COMPONENTS ====================

const StepBasicData = ({ formData, onChange }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-full bg-[#8F7AC5] flex items-center justify-center">
        <Baby className="w-5 h-5 text-white" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-[#30244F]">Dados do Bebê e Família</h2>
        <p className="text-sm text-[#6B5B8C]">Informações básicas para o relatório</p>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Nome do Bebê */}
      <div className="space-y-2">
        <Label className="text-[#30244F]">Nome do Bebê *</Label>
        <Input
          value={formData.babyName}
          onChange={(e) => onChange('babyName', e.target.value)}
          placeholder="Ex: Maria"
          className="border-[#FAE0D5] focus:border-[#8F7AC5]"
        />
      </div>

      {/* Idade em meses */}
      <div className="space-y-2">
        <Label className="text-[#30244F]">Idade (meses) *</Label>
        <Input
          type="number"
          value={formData.babyAge}
          onChange={(e) => onChange('babyAge', e.target.value)}
          placeholder="Ex: 8"
          min="0"
          max="36"
          className="border-[#FAE0D5] focus:border-[#8F7AC5]"
        />
      </div>

      {/* Nome da Mãe */}
      <div className="space-y-2">
        <Label className="text-[#30244F]">Nome da Mãe *</Label>
        <Input
          value={formData.motherName}
          onChange={(e) => onChange('motherName', e.target.value)}
          placeholder="Ex: Ana Silva"
          className="border-[#FAE0D5] focus:border-[#8F7AC5]"
        />
      </div>

      {/* Nome do Pai */}
      <div className="space-y-2">
        <Label className="text-[#30244F]">Nome do Pai</Label>
        <Input
          value={formData.fatherName}
          onChange={(e) => onChange('fatherName', e.target.value)}
          placeholder="Ex: João Silva"
          className="border-[#FAE0D5] focus:border-[#8F7AC5]"
        />
      </div>

      {/* E-mail */}
      <div className="space-y-2">
        <Label className="text-[#30244F]">E-mail da Mãe</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder="Ex: mae@email.com"
          className="border-[#FAE0D5] focus:border-[#8F7AC5]"
        />
      </div>

      {/* WhatsApp */}
      <div className="space-y-2">
        <Label className="text-[#30244F]">WhatsApp</Label>
        <Input
          value={formData.whatsapp}
          onChange={(e) => onChange('whatsapp', e.target.value)}
          placeholder="Ex: (11) 99999-9999"
          className="border-[#FAE0D5] focus:border-[#8F7AC5]"
        />
      </div>

      {/* Semanas de gestação */}
      <div className="space-y-2">
        <Label className="text-[#30244F]">Nasceu com quantas semanas?</Label>
        <Input
          type="number"
          value={formData.birthWeeks}
          onChange={(e) => onChange('birthWeeks', e.target.value)}
          placeholder="Ex: 39"
          min="20"
          max="45"
          className="border-[#FAE0D5] focus:border-[#8F7AC5]"
        />
      </div>

      {/* Data de nascimento */}
      <div className="space-y-2">
        <Label className="text-[#30244F]">Data de Nascimento</Label>
        <Input
          type="date"
          value={formData.birthDate}
          onChange={(e) => onChange('birthDate', e.target.value)}
          className="border-[#FAE0D5] focus:border-[#8F7AC5]"
        />
      </div>
    </div>
  </div>
);

const StepQuestionnaire = ({ formData, onChange }) => {
  // Função para toggle de opção no array howBabySleeps
  const toggleHowBabySleeps = (option) => {
    const current = Array.isArray(formData.howBabySleeps) ? formData.howBabySleeps : [];
    if (current.includes(option)) {
      onChange('howBabySleeps', current.filter(o => o !== option));
    } else {
      onChange('howBabySleeps', [...current, option]);
    }
  };

  return (
  <div className="space-y-6">
    {/* Sobre o Sono */}
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-[#D98DAE] flex items-center justify-center">
          <Moon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-[#30244F]">Sobre o Sono</h2>
          <p className="text-sm text-[#6B5B8C]">Informações sobre os hábitos de sono atuais</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Maior dificuldade */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Qual a maior dificuldade com o sono? *</Label>
          <Textarea
            value={formData.sleepDifficulty}
            onChange={(e) => onChange('sleepDifficulty', e.target.value)}
            placeholder="Descreva a principal dificuldade..."
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[80px]"
          />
        </div>

        {/* Expectativas */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Quais são as expectativas?</Label>
          <Textarea
            value={formData.expectations}
            onChange={(e) => onChange('expectations', e.target.value)}
            placeholder="O que esperam alcançar com a consultoria..."
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[80px]"
          />
        </div>

        {/* Como o bebê dorme - MULTI-SELECT */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Como o bebê adormece atualmente? * (selecione todas que se aplicam)</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            {howBabySleepsOptions.map(opt => {
              const isSelected = Array.isArray(formData.howBabySleeps) && formData.howBabySleeps.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggleHowBabySleeps(opt)}
                  className={`p-3 rounded-lg border text-sm text-left transition-all ${
                    isSelected 
                      ? 'border-[#8F7AC5] bg-[#8F7AC5]/10 text-[#30244F] font-medium' 
                      : 'border-[#FAE0D5] bg-white text-[#6B5B8C] hover:border-[#D98DAE]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${
                      isSelected ? 'bg-[#8F7AC5] border-[#8F7AC5]' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {opt}
                  </span>
                </button>
              );
            })}
          </div>
          {Array.isArray(formData.howBabySleeps) && formData.howBabySleeps.length > 0 && (
            <p className="text-xs text-[#6B5B8C] mt-2">
              Selecionados: {formData.howBabySleeps.join(', ')}
            </p>
          )}
        </div>

        {/* Onde dorme */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Onde o bebê dorme?</Label>
          <Select value={formData.sleepLocation} onValueChange={(v) => onChange('sleepLocation', v)}>
            <SelectTrigger className="border-[#FAE0D5]">
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              {whereBabySleepsOptions.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Objeto de transição */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Usa objeto de transição (naninha, chupeta)?</Label>
          <Select value={formData.transitionObject} onValueChange={(v) => onChange('transitionObject', v)}>
            <SelectTrigger className="border-[#FAE0D5]">
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Sim</SelectItem>
              <SelectItem value="no">Não</SelectItem>
            </SelectContent>
          </Select>
          {formData.transitionObject === 'yes' && (
            <Input
              value={formData.transitionObjectDesc}
              onChange={(e) => onChange('transitionObjectDesc', e.target.value)}
              placeholder="Qual objeto?"
              className="mt-2 border-[#FAE0D5] focus:border-[#8F7AC5]"
            />
          )}
        </div>

        {/* Tentativas anteriores */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Já tentaram algum método antes?</Label>
          <Textarea
            value={formData.previousAttempts}
            onChange={(e) => onChange('previousAttempts', e.target.value)}
            placeholder="Descreva tentativas anteriores..."
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[60px]"
          />
        </div>
      </div>
    </div>

    {/* Saúde */}
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-[#7BC47F] flex items-center justify-center">
          <Heart className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-[#30244F]">Saúde do Bebê</h2>
          <p className="text-sm text-[#6B5B8C]">Informações de saúde importantes</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Condições de saúde */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Condições de saúde (alergias, refluxo, prematuridade, etc.)</Label>
          <Textarea
            value={formData.healthConditions}
            onChange={(e) => onChange('healthConditions', e.target.value)}
            placeholder="Descreva ou escreva 'Não possui'"
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[60px]"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Teste do pezinho */}
          <div className="space-y-2">
            <Label className="text-[#30244F]">Alguma alteração no teste do pezinho?</Label>
            <Select value={formData.heelPrickTest} onValueChange={(v) => onChange('heelPrickTest', v)}>
              <SelectTrigger className="border-[#FAE0D5]">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Normal">Normal</SelectItem>
                <SelectItem value="Alterado">Alterado</SelectItem>
                <SelectItem value="Não realizado">Não realizado</SelectItem>
                <SelectItem value="Não sei">Não sei</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Ronco */}
          <div className="space-y-2">
            <Label className="text-[#30244F]">Ronca na madrugada?</Label>
            <Select value={formData.snoring} onValueChange={(v) => onChange('snoring', v)}>
              <SelectTrigger className="border-[#FAE0D5]">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Não">Não</SelectItem>
                <SelectItem value="Sim">Sim</SelectItem>
                <SelectItem value="Às vezes">Às vezes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Suplementação de ferro e vitamina D */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Suplementação: O bebê suplementa ferro e vitamina D?</Label>
          <Textarea
            value={formData.ironSupplementation}
            onChange={(e) => onChange('ironSupplementation', e.target.value)}
            placeholder="Se sim, qual horário? Ex: Sim, ferro às 10h e vitamina D às 8h"
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[60px]"
          />
        </div>
      </div>
    </div>

    {/* Alimentação */}
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-[#F5A623] flex items-center justify-center">
          <Utensils className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-[#30244F]">Alimentação</h2>
          <p className="text-sm text-[#6B5B8C]">Informações sobre alimentação</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Tipo de alimentação */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Tipo de alimentação</Label>
          <Select value={formData.feedingType} onValueChange={(v) => onChange('feedingType', v)}>
            <SelectTrigger className="border-[#FAE0D5]">
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exclusivo_peito">Amamentação exclusiva</SelectItem>
              <SelectItem value="misto">Misto (peito + fórmula)</SelectItem>
              <SelectItem value="formula">Fórmula</SelectItem>
              <SelectItem value="solidos">Já come sólidos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Introdução alimentar */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Introdução alimentar</Label>
          <Textarea
            value={formData.solidFoods}
            onChange={(e) => onChange('solidFoods', e.target.value)}
            placeholder="Já começou? Como está sendo?"
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[60px]"
          />
        </div>
      </div>
    </div>

    {/* Contexto Familiar */}
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-[#8F7AC5] flex items-center justify-center">
          <User className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-[#30244F]">Contexto Familiar</h2>
          <p className="text-sm text-[#6B5B8C]">Informações sobre a família e rotina</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Desenvolvimento motor */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Desenvolvimento motor atual</Label>
          <Textarea
            value={formData.motorDevelopment}
            onChange={(e) => onChange('motorDevelopment', e.target.value)}
            placeholder="Rola, engatinha, anda, escala..."
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[60px]"
          />
        </div>

        {/* Quem cuida */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Quem cuida do bebê? Tem ajuda?</Label>
          <Textarea
            value={formData.caregiver}
            onChange={(e) => onChange('caregiver', e.target.value)}
            placeholder="Descreva quem cuida durante o dia, se tem babá, avós, se a mãe trabalha fora..."
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[60px]"
          />
        </div>

        {/* Outros filhos */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Tem outros filhos? Como é a dinâmica?</Label>
          <Textarea
            value={formData.otherChildren}
            onChange={(e) => onChange('otherChildren', e.target.value)}
            placeholder="Conte sobre a rotina da casa com os outros filhos..."
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[60px]"
          />
        </div>

        {/* Sono da mãe */}
        <div className="space-y-2">
          <Label className="text-[#30244F]">Como está o sono da mãe? Está afetando seu bem-estar?</Label>
          <Textarea
            value={formData.motherSleep}
            onChange={(e) => onChange('motherSleep', e.target.value)}
            placeholder="Seja honesta, isso é importante para o plano..."
            className="border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[60px]"
          />
        </div>
      </div>
    </div>
  </div>
)};

const StepRoutineDay = ({ day, dayNumber, onChange, onAddNap, onRemoveNap, onUpdateNap }) => {
  // Opções para os Selects
  const nightWakingsOptions = [
    '0 - Dormiu a noite toda',
    '1 vez',
    '2 vezes',
    '3 vezes',
    '4 vezes',
    '5 ou mais vezes'
  ];

  const nightWakingsDurationOptions = [
    'Menos de 5 minutos',
    '5-10 minutos',
    '10-20 minutos',
    '20-30 minutos',
    '30-60 minutos',
    'Mais de 1 hora'
  ];

  const napDurationOptions = [
    '15 minutos',
    '20 minutos',
    '30 minutos',
    '45 minutos',
    '1 hora',
    '1h30',
    '2 horas',
    '2h30 ou mais'
  ];

  return (
    <div className="space-y-6">
      {/* Card principal - Sono Noturno */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] flex items-center justify-center">
            <span className="text-white font-bold">{dayNumber}</span>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#30244F]">Dia {dayNumber} - Sono Noturno</h2>
            <p className="text-sm text-[#6B5B8C]">Horários e despertares da noite</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Horários obrigatórios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#30244F] flex items-center gap-2">
                <Sun className="w-4 h-4 text-[#F5C542]" />
                Horário que acordou *
              </Label>
              <Input
                type="time"
                value={day.wakeUpTime}
                onChange={(e) => onChange('wakeUpTime', e.target.value)}
                className="border-[#FAE0D5] focus:border-[#8F7AC5]"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#30244F] flex items-center gap-2">
                <Moon className="w-4 h-4 text-[#8F7AC5]" />
                Horário que dormiu *
              </Label>
              <Input
                type="time"
                value={day.bedTime}
                onChange={(e) => onChange('bedTime', e.target.value)}
                className="border-[#FAE0D5] focus:border-[#8F7AC5]"
              />
            </div>
          </div>

          {/* Despertares noturnos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#30244F] text-sm">Quantas vezes acordou à noite?</Label>
              <Select value={day.nightWakings} onValueChange={(v) => onChange('nightWakings', v)}>
                <SelectTrigger className="border-[#FAE0D5]">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {nightWakingsOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#30244F] text-sm">Duração dos despertares</Label>
              <Select value={day.nightWakingsDuration} onValueChange={(v) => onChange('nightWakingsDuration', v)}>
                <SelectTrigger className="border-[#FAE0D5]">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {nightWakingsDurationOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Card de Sonecas */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-[#F2ACAA]" />
            <h3 className="font-semibold text-[#30244F]">Sonecas do Dia</h3>
          </div>
          {!day.noNaps && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onAddNap(dayNumber - 1)}
              className="text-[#8F7AC5] hover:bg-[#8F7AC5]/10"
            >
              <Plus className="w-4 h-4 mr-1" />
              Adicionar
            </Button>
          )}
        </div>

        {/* Toggle não fez soneca */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => onChange('noNaps', !day.noNaps)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
              day.noNaps
                ? 'bg-[#F2ACAA] text-white'
                : 'bg-[#FBF4FF] text-[#6B5B8C] hover:bg-[#F2ACAA]/20'
            }`}
          >
            {day.noNaps && <Check className="w-4 h-4" />}
            Não fez soneca hoje
          </button>
        </div>

        {/* Lista de sonecas */}
        {!day.noNaps && (
          <div className="space-y-4">
            {day.naps && day.naps.length > 0 ? (
              day.naps.map((nap, napIndex) => (
                <div key={napIndex} className="bg-[#FBF4FF] rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#8F7AC5]">Soneca {napIndex + 1}</span>
                    {day.naps.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveNap(dayNumber - 1, napIndex)}
                        className="text-red-400 hover:text-red-600 h-6 w-6 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#6B5B8C]">Horário</Label>
                      <Input
                        type="time"
                        value={nap.startTime || ''}
                        onChange={(e) => onUpdateNap(dayNumber - 1, napIndex, 'startTime', e.target.value)}
                        className="mt-1 text-sm border-[#D98DAE]/30"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#6B5B8C]">Duração</Label>
                      <Select 
                        value={nap.duration || ''} 
                        onValueChange={(v) => onUpdateNap(dayNumber - 1, napIndex, 'duration', v)}
                      >
                        <SelectTrigger className="mt-1 text-sm border-[#D98DAE]/30">
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {napDurationOptions.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-[#9890A8] text-center py-4">
                Clique em "Adicionar" para registrar uma soneca
              </p>
            )}
          </div>
        )}
      </div>

      {/* Observações */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
        <Label className="text-[#30244F]">Observações do dia (opcional)</Label>
        <Textarea
          value={day.notes}
          onChange={(e) => onChange('notes', e.target.value)}
          placeholder="Outros acontecimentos importantes, como o bebê estava, etc..."
          className="mt-2 border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[80px]"
        />
      </div>
    </div>
  );
};

const StepReview = ({ formData, routineDays }) => (
  <div className="space-y-6">
    {/* Dados do Bebê */}
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
      <h3 className="text-lg font-semibold text-[#30244F] mb-4 flex items-center gap-2">
        <Baby className="w-5 h-5 text-[#8F7AC5]" />
        Dados do Bebê
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-[#6B5B8C]">Nome:</span>
          <p className="font-medium text-[#30244F]">{formData.babyName || '-'}</p>
        </div>
        <div>
          <span className="text-[#6B5B8C]">Idade:</span>
          <p className="font-medium text-[#30244F]">{formData.babyAge ? `${formData.babyAge} meses` : '-'}</p>
        </div>
        <div>
          <span className="text-[#6B5B8C]">Mãe:</span>
          <p className="font-medium text-[#30244F]">{formData.motherName || '-'}</p>
        </div>
      </div>
    </div>

    {/* Questionário */}
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
      <h3 className="text-lg font-semibold text-[#30244F] mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-[#D98DAE]" />
        Questionário
      </h3>
      <div className="space-y-3 text-sm">
        <div>
          <span className="text-[#6B5B8C]">Dificuldade:</span>
          <p className="font-medium text-[#30244F]">{formData.sleepDifficulty || '-'}</p>
        </div>
        <div>
          <span className="text-[#6B5B8C]">Como dorme:</span>
          <p className="font-medium text-[#30244F]">{Array.isArray(formData.howBabySleeps) ? formData.howBabySleeps.join(', ') : formData.howBabySleeps || '-'}</p>
        </div>
        <div>
          <span className="text-[#6B5B8C]">Onde dorme:</span>
          <p className="font-medium text-[#30244F]">{formData.sleepLocation || '-'}</p>
        </div>
      </div>
    </div>

    {/* Rotina dos 5 dias */}
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#FAE0D5]">
      <h3 className="text-lg font-semibold text-[#30244F] mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-[#7BC47F]" />
        Rotina dos 5 Dias
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {routineDays.map((day, index) => (
          <div key={index} className="bg-[#FBF4FF] rounded-xl p-3 text-center">
            <div className="font-semibold text-[#8F7AC5] mb-2">Dia {day.day}</div>
            <div className="text-xs text-[#6B5B8C] space-y-1">
              <div className="flex items-center justify-center gap-1">
                <Sun className="w-3 h-3 text-[#F5C542]" />
                {day.wakeUpTime || '--:--'}
              </div>
              <div className="flex items-center justify-center gap-1">
                <Moon className="w-3 h-3 text-[#8F7AC5]" />
                {day.bedTime || '--:--'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Aviso */}
    <div className="bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] rounded-2xl p-6 text-white">
      <div className="flex items-start gap-4">
        <Sparkles className="w-8 h-8 flex-shrink-0" />
        <div>
          <h4 className="font-semibold text-lg mb-2">Pronto para gerar o plano!</h4>
          <p className="text-white/90 text-sm">
            Ao clicar em "Criar e Gerar Plano", os dados serão salvos e você será direcionado 
            para a tela de geração do plano personalizado com IA.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default AdminManualReportPage;
